"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { ApiError, api, loadTokens } from "@/lib/api";

/**
 * EPIC-044 follow-up — small Join / Following button rendered on each
 * /topics index card. Receives the initial state from the server's
 * batched followed-topics lookup (no per-card round-trip).
 *
 * Anonymous viewers see "Join" but click → /login redirect. Authed
 * users get optimistic toggle + revert-on-failure.
 */
export function TopicJoinButton({
  slug,
  initiallyFollowing,
  initialFollowerCount,
}: {
  slug: string;
  initiallyFollowing: boolean;
  initialFollowerCount: number;
}) {
  const router = useRouter();
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [following, setFollowing] = useState(initiallyFollowing);
  const [submitting, setSubmitting] = useState(false);
  // We track follower count purely for revert-on-failure correctness;
  // the visible count on the card stays the server-rendered one
  // (re-renders on next page load).
  const [, setFollowerCount] = useState(initialFollowerCount);

  useEffect(() => {
    setSignedIn(loadTokens() !== null);
  }, []);

  async function handleClick(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!signedIn) {
      router.push(`/login?next=/topics`);
      return;
    }
    if (submitting) return;
    const wasFollowing = following;
    setFollowing(!wasFollowing);
    setFollowerCount((c) => c + (wasFollowing ? -1 : 1));
    setSubmitting(true);
    try {
      if (wasFollowing) {
        await api.unfollowTopic(slug);
      } else {
        await api.followTopic(slug);
      }
    } catch (err) {
      setFollowing(wasFollowing);
      setFollowerCount((c) => c + (wasFollowing ? 1 : -1));
      if (err instanceof ApiError && err.status === 401) {
        router.push(`/login?next=/topics`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (signedIn === null) {
    // SSR fallback — render the button in its server-known state with
    // no interactivity yet. Hydration replaces it with the live one.
    return (
      <button
        type="button"
        disabled
        className="flex-shrink-0 rounded-full border px-3 py-1 text-[11px] font-bold opacity-50"
        style={{
          borderColor: "var(--color-brand)",
          color: "var(--color-brand)",
        }}
      >
        {initiallyFollowing ? "Following" : "Join"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={submitting}
      aria-pressed={following}
      aria-label={following ? "Unfollow topic" : "Follow topic"}
      className="flex-shrink-0 rounded-full border px-3 py-1 text-[11px] font-bold transition-colors disabled:opacity-50"
      style={{
        borderColor: following
          ? "var(--color-border)"
          : "var(--color-brand)",
        background: following ? "transparent" : "var(--color-brand)",
        color: following ? "var(--color-text2)" : "white",
      }}
    >
      {following ? "Following" : "Join"}
    </button>
  );
}
